import React, { useEffect, useState } from 'react';

export const AnalyticsReportSection: React.FC<{
    API_BASE: string;
    authHeaders: () => Record<string, string>;
}> = ({ API_BASE, authHeaders }) => {
    const [recipients, setRecipients] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    useEffect(() => {
        fetch(`${API_BASE}/admin/analytics-report-recipients`, { headers: authHeaders() })
            .then((r) => r.json())
            .then((d) => setRecipients(d.recipients ?? []))
            .catch(() => setStatus({ type: 'error', text: 'Failed to load recipients.' }))
            .finally(() => setLoading(false));
        // API_BASE is a module-level constant and authHeaders is a stable
        // function reference — neither will change between renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = async (updated: string[]) => {
        setSaving(true);
        setStatus(null);
        try {
            const res = await fetch(`${API_BASE}/admin/analytics-report-recipients`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ recipients: updated }),
            });
            if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Save failed.');
            setRecipients(updated);
            setStatus({ type: 'success', text: 'Recipients updated.' });
        } catch (err: any) {
            setStatus({ type: 'error', text: err.message || 'Save failed.' });
        } finally {
            setSaving(false);
        }
    };

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        const email = newEmail.trim().toLowerCase();
        if (!email) return;
        if (recipients.includes(email)) {
            setStatus({ type: 'error', text: `${email} is already in the list.` });
            return;
        }
        setNewEmail('');
        await save([...recipients, email]);
    };

    const handleRemove = async (email: string) => {
        await save(recipients.filter((r) => r !== email));
    };

    return (
        <div>
            <h2>Daily Analytics Report Recipients</h2>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                A summary email is sent every day at 00:30 UTC with DAU, MAU, posts, memories, invites, and viral metrics.
            </p>

            {loading ? (
                <p className="status">Loading...</p>
            ) : (
                <>
                    {recipients.length === 0 ? (
                        <p className="status" style={{ color: '#999' }}>No recipients configured. Reports are paused until at least one address is added.</p>
                    ) : (
                        <ul style={{ listStyle: 'none', padding: 0, marginBottom: '1rem' }}>
                            {recipients.map((email) => (
                                <li key={email} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span style={{ flex: 1, fontSize: '0.95rem' }}>{email}</span>
                                    <button
                                        onClick={() => handleRemove(email)}
                                        disabled={saving}
                                        className="btn btn-danger"
                                        style={{ padding: '4px 12px', fontSize: '0.8rem' }}
                                    >
                                        Remove
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    <form onSubmit={handleAdd} style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                        <input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="name@example.com"
                            className="dashboard-input"
                            style={{ flex: 1 }}
                            required
                        />
                        <button type="submit" disabled={saving} className="btn btn-primary">
                            {saving ? 'Saving...' : 'Add'}
                        </button>
                    </form>
                </>
            )}

            {status && (
                <p className={`status${status.type === 'error' ? ' status-error' : ''}`} style={{ marginTop: '0.75rem' }}>
                    {status.text}
                </p>
            )}
        </div>
    );
};
